/**
 * @file contentSource.js
 * @description 주입 전역과 조회 응답을 콘텐츠 값 맵으로 해석합니다.
 * @used-in content/ContentProvider.jsx, content/useContent.js
 */

const EMPTY_VALUES = Object.freeze({});

// [기능] 배열이나 null을 제외한 일반 데이터 객체인지 확인합니다.
function isRecord(value) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

// [기능] 호출 시점에 서버 주입 전역의 존재 여부를 반환합니다.
export function hasInjectedContent() {
  // [비즈니스] 빈 맵도 정상 운영 주입이므로 전역 자체가 없을 때만 조회합니다.
  return typeof window !== 'undefined' && typeof window.__SH_CONTENT__ !== 'undefined';
}

// [기능] 현재 주입된 값 맵을 읽고 잘못된 입력에는 공용 빈 맵을 반환합니다.
export function readInjectedValues() {
  // [기술] 모듈 로드 시 캐시하지 않아 호출마다 최신 주입값을 읽습니다.
  if (!hasInjectedContent()) return EMPTY_VALUES;
  const record = window.__SH_CONTENT__;
  return isRecord(record) && isRecord(record.values) ? record.values : EMPTY_VALUES;
}

// [기능] 저장 레코드에서 문자열과 문자열 목록을 추출한 평탄 맵을 반환합니다.
export function flattenContentValues(record) {
  // [기술] __proto__도 안전하게 일반 키로 보관하도록 프로토타입 없는 맵을 씁니다.
  const values = Object.create(null);
  if (!isRecord(record) || !isRecord(record.values)) return values;
  for (const [key, entry] of Object.entries(record.values)) {
    // [비즈니스] 수동 입력 문자열도 수용하지만 잘못된 값은 버리고 baseHash는 공개하지 않습니다.
    if (typeof entry === 'string') values[key] = entry;
    else if (isRecord(entry) && typeof entry.v === 'string') values[key] = entry.v;
    // [비즈니스] 목록은 모든 항목이 문자열일 때만 수용하고 맨 배열은 계속 버립니다.
    else if (isRecord(entry) && Array.isArray(entry.items) && entry.items.every((s) => typeof s === 'string')) {
      values[key] = entry.items;
    }
  }
  return values;
}
